'use client';

import { usePatientSelection } from '@/app/(main)/_components/patient-selection/_hooks/use-patient-selection.hook';
import { useToast } from '@/components/ui/use-toast';
import { getLogger } from '@/logger/logger';
import { changePatientOnCartItems } from '@/store/cart/cart.store';
import { CartItem, CartPatient } from '@/types/cart.types';
import { CartChangePatientScope } from '@/types/enums/analytics/cart/cart-analytics.enum';
import { PatientOption } from '@/types/patient-selection.types';
import { trackChangePatientOpened, trackPatientChanged } from '@/utils/cart/cart-analytics.util';

export const CHANGE_CART_PATIENT_ERROR_MESSAGE = 'Não foi possível trocar o paciente. Tente novamente.';

const logger = getLogger('cart.change-patient');

export interface UseChangeCartPatientResult {
  changeItemPatient: (item: CartItem) => void;
  changeGroupPatient: (items: CartItem[]) => void;
}

function toCartPatient(patient: PatientOption): CartPatient {
  return {
    id: patient.id,
    legalName: patient.name,
    socialName: '',
    documentNumber: patient.documentNumber,
  };
}

export function useChangeCartPatient(): UseChangeCartPatientResult {
  const { requestPatientSelection } = usePatientSelection();
  const { toast } = useToast();

  function requestChange(items: CartItem[], scope: CartChangePatientScope): void {
    if (items.length === 0) {
      return;
    }

    const [{ slot }] = items;
    const cartItemsIds = items.map((item) => item.id);

    trackChangePatientOpened(scope, items);

    requestPatientSelection({
      ageRange: { minAge: slot.minAge, maxAge: slot.maxAge },
      canSearchAnotherOption: false,
      onSelectPatient: (patient) => {
        void applyChange(items, cartItemsIds, patient, scope);
      },
    });
  }

  async function applyChange(
    items: CartItem[],
    cartItemsIds: string[],
    patient: PatientOption,
    scope: CartChangePatientScope,
  ): Promise<void> {
    try {
      await changePatientOnCartItems(cartItemsIds, toCartPatient(patient));

      trackPatientChanged(scope, items, patient);
    } catch (error) {
      logger.error('Failed to change the patient on cart items', {
        cart: {
          event: 'patient_change_failed',
          scope,
          cart_item_ids: cartItemsIds,
          patient_id: patient.id,
        },
        error,
      });
      toast.error(CHANGE_CART_PATIENT_ERROR_MESSAGE);
    }
  }

  return {
    changeItemPatient: (item: CartItem) => requestChange([item], CartChangePatientScope.ITEM),
    changeGroupPatient: (items: CartItem[]) => requestChange(items, CartChangePatientScope.GROUP),
  };
}
